import { useEffect, useRef } from 'react';

/**
 * Idle sign-out (Admin: 30 minutes, from SESSION_POLICY in packages/domain).
 *
 * Only real user input counts as activity: pointer, keyboard, wheel, touch and scroll events.
 * Network requests (including React Query fetches) never touch the timer.
 *
 * Cheap by design: activity only writes a timestamp into a ref (no re-render, no timer churn).
 * One timeout is armed for the earliest moment the user could be idle; when it fires it re-checks
 * the timestamp and either signs out or re-arms for the remaining time. Coming back to a hidden
 * tab re-checks immediately, because background timers may be throttled.
 *
 * `onActiveAfterQuiet` fires on the first activity after `keepAliveAfterMs` without any: the
 * caller uses it to re-read the session, so the server's own idle timeout follows genuine use
 * rather than timers.
 */
export const ACTIVITY_EVENTS = [
  'pointerdown',
  'pointermove',
  'keydown',
  'wheel',
  'touchstart',
  'scroll',
] as const;

export interface IdleTimeoutOptions {
  timeoutMs: number;
  onIdle: () => void;
  keepAliveAfterMs?: number;
  onActiveAfterQuiet?: () => void;
  /** Tests only. */
  now?: () => number;
}

export function useIdleTimeout(options: IdleTimeoutOptions): void {
  // Latest callbacks without re-subscribing listeners on every render.
  const latest = useRef(options);
  useEffect(() => {
    latest.current = options;
  });

  const { timeoutMs } = options;

  useEffect(() => {
    const now = () => (latest.current.now ?? Date.now)();
    let lastActivity = now();
    let lastKeepAlive = lastActivity;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let fired = false;

    const check = () => {
      if (fired) return;
      const idleFor = now() - lastActivity;
      if (idleFor >= timeoutMs) {
        fired = true;
        latest.current.onIdle();
        return;
      }
      timer = setTimeout(check, timeoutMs - idleFor);
    };

    const onActivity = () => {
      if (fired) return;
      const t = now();
      lastActivity = t;
      const { keepAliveAfterMs, onActiveAfterQuiet } = latest.current;
      if (
        keepAliveAfterMs !== undefined &&
        onActiveAfterQuiet &&
        t - lastKeepAlive >= keepAliveAfterMs
      ) {
        lastKeepAlive = t;
        onActiveAfterQuiet();
      }
    };

    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      clearTimeout(timer);
      check();
    };

    const listenerOptions: AddEventListenerOptions = { passive: true, capture: true };
    for (const event of ACTIVITY_EVENTS)
      window.addEventListener(event, onActivity, listenerOptions);
    document.addEventListener('visibilitychange', onVisibility);
    timer = setTimeout(check, timeoutMs);

    return () => {
      clearTimeout(timer);
      for (const event of ACTIVITY_EVENTS) {
        window.removeEventListener(event, onActivity, listenerOptions);
      }
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [timeoutMs]);
}
